import {
  doc,
  DocumentData,
  DocumentReference,
  FirestoreDataConverter,
  getFirestore,
  QueryDocumentSnapshot,
  SnapshotOptions,
  Timestamp,
  type CollectionReference,
  collection,
} from 'firebase/firestore';
import {inferUnitTestTypeFromMetadata} from 'ide/UnitTest/runUnitTests';
import {
  AssignmentUnitTestType,
  AssignmentType,
  AssignmentUnitTestBehavior,
  AssignmentId,
  ShowSolutionBehavior,
  isShowSolutionBehavior,
} from 'assignments/types';
import {CourseId} from 'types/common';
import {isNullOrUndefined} from 'utils/general';
import {
  AssignmentMilestonesSchema,
  type AssignmentMilestone,
} from '@cip/core/assignments';
import type {MathIdeExperience} from '@cip/core/mathide';

/**
 * The metadata for an assignment. If you are adding a new field, make sure
 * to update the `isAssignmentMetadata` function to check for the new field.
 */
export interface AssignmentMetadata {
  /** A human-readable name for the assignment. */
  title: string;
  /** The unique ID for the assignment. Also called the "assignment id". */
  uid: string;
  /** The type of assignment. */
  type: AssignmentType;
  /** The autograder type for the assignment. */
  unitTestType: AssignmentUnitTestType;
  /** When to run the autograder for the assignment. */
  unitTestBehavior: AssignmentUnitTestBehavior;
  /**
   * @deprecated Use `showSolutionBehavior` instead. Whether the assignment is
   * a worked example (i.e. has provided solution code). Defaults as `false`
   * when missing.
   */
  workedExample?: boolean;
  /** Tags for the assignment to help group them (e.g. "easy", "loops"). */
  tags?: string[];
  /**
   * Ordered checkpoints, each owning its own unit tests (matched by
   * `milestoneId` in `docs/unitTests`) and its own banked completion. Omitted,
   * the assignment is graded as a whole. Ignored unless the unit test type is
   * console; read it through {@link getGradedMilestones}. See
   * `@cip/core/assignments` for the selection and progress-key rules.
   */
  milestones?: AssignmentMilestone[];
  /** An optional link to show to students after submitting an assignment.
   * This can be used, for example, to direct students to an external upload
   * page.
   */
  onSubmitLink?: string;
  /** The Canvas assignment ID for the assignment. */
  canvasAssnId?: number;
  /** Whether to show the solution behavior in the IDE. */
  showSolutionBehavior?: ShowSolutionBehavior;
  /**
   * Whether to skip the Karel compiler. Only used for Karel assignments.
   * Defaults to false.
   */
  skipKarelCompiler?: boolean;
  /**
   * Whether to ignore case when comparing student code to the solution code.
   * Defaults to false.
   */
  shouldIgnoreCasingInAutograder?: boolean;
  /**
   * Whether to ignore whitespace when comparing student code to the solution code.
   * Defaults to false.
   */
  shouldIgnoreWhitespaceInAutograder?: boolean;
  /**
   * @deprecated Use `unitTestType` instead. Legacy field to make an assignment
   * use AI autograders.
   */
  isRandom?: boolean;
  /**
   * Whether to show the instructor-authored math hint in the Math IDE center
   * panel. Only applies to `type: 'mathide'` assignments. Defaults to `true`
   * when omitted.
   */
  showHint?: boolean;
  /**
   * Default width of the test-results panel in pixels for the Math IDE alt
   * and draggable layouts. Only applies to `type: 'mathide'` assignments.
   */
  preferredResultsWidth?: number;
  /**
   * Whether human code editing is locked in the Math IDE Code panel — greys
   * out the file tree with a lock icon and keeps the Monaco editor
   * read-only. Only applies to `type: 'mathide'` assignments. Defaults to
   * `false` (editable) when omitted.
   */
  codeEditingLocked?: boolean;
  /**
   * Explanation shown next to the lock icon, and available to the AI tutor
   * if a student asks why. Ignored when `codeEditingLocked` is false/omitted.
   */
  codeEditingLockedReason?: string;
  /**
   * Model the AI-text agent should run this assignment on, e.g.
   * `gemini-3.1-pro-preview` or `gpt-5.1`. The provider follows from the id.
   *
   * Omitted means the agent's own default. The server re-checks this against
   * its allowlist (`freeFlowServer/models.ts`) and falls back to that default
   * for anything it does not recognise, so a typo here degrades to the default
   * rather than breaking every turn — and this field can only ever select a
   * model that shipped in a deploy.
   *
   * This exists so an assignment can be moved between models, and between
   * providers, without a deploy.
   * See [#4457](https://github.com/CodeinPlace/cip/issues/4457).
   */
  agentModel?: string;
  /**
   * Which Math IDE experience renders this assignment. `'agent-choice'`
   * selects the generic agent-choice experience, whose whole content — persona,
   * milestones, parameters — is the assignment's `docs/agentFlow` document and
   * whose "Your App" is its `docs/reactFiles`, so a new assignment of that
   * kind needs no deploy. Omitted keeps the existing behavior. Set from the
   * Math Assignment Editor's "Agent Flow" section.
   * See [#4431](https://github.com/CodeinPlace/cip/issues/4431).
   */
  mathIdeExperience?: MathIdeExperience;
  /**
   * A picture shown on the agent-choice assignment's splash page, with its
   * alt text; set from the assignment folder's `assignment.json`. Omitted
   * shows no picture.
   */
  splashImage?: {src: string; alt: string};
  /**
   * The icon a `One of` roadmap item's chooser draws on this assignment's
   * card, a key of `SUPPORTED_TODO_ICON_OVERRIDES`; set from the assignment
   * folder's `assignment.json`. Omitted or unknown draws the rocket.
   */
  icon?: string;
  /**
   * One line on what the student does here, shown under the title on a
   * `One of` chooser card; set from the assignment folder's
   * `assignment.json`. Omitted shows the title alone.
   */
  tagline?: string;
  /**
   * A short line course staff write about this assignment, shown under the
   * tagline on a `One of` chooser card; set from the assignment folder's
   * `assignment.json`. Omitted or blank shows nothing.
   */
  chooserNote?: string;
}

export function isAssignmentMetadata(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: [#915](https://github.com/CodeinPlace/cip-webapp/issues/915) - fix legacy lint issue
  metadata: any,
): metadata is AssignmentMetadata {
  return (
    metadata &&
    typeof metadata === 'object' &&
    typeof metadata.title === 'string' &&
    typeof metadata.uid === 'string' &&
    typeof metadata.type === 'string' &&
    typeof metadata.unitTestType === 'string' &&
    typeof metadata.unitTestBehavior === 'string' &&
    (isNullOrUndefined(metadata.workedExample) ||
      typeof metadata.workedExample === 'boolean') &&
    (isNullOrUndefined(metadata.tags) ||
      (Array.isArray(metadata.tags) &&
        metadata.tags.every(tag => typeof tag === 'string'))) &&
    (isNullOrUndefined(metadata.milestones) ||
      AssignmentMilestonesSchema.safeParse(metadata.milestones).success) &&
    (isNullOrUndefined(metadata.onSubmitLink) ||
      typeof metadata.onSubmitLink === 'string') &&
    (isNullOrUndefined(metadata.canvasAssnId) ||
      typeof metadata.canvasAssnId === 'number') &&
    (isNullOrUndefined(metadata.showSolutionBehavior) ||
      isShowSolutionBehavior(metadata.showSolutionBehavior)) &&
    (isNullOrUndefined(metadata.skipKarelCompiler) ||
      typeof metadata.skipKarelCompiler === 'boolean')
  );
}

/**
 * Reads the milestones an assignment is graded by. Only console unit tests can
 * carry a `milestoneId`, so for any other unit test type the list is ignored:
 * each milestone check would run the whole suite and bank a single step.
 *
 * @param args - The assignment to read.
 * @param args.assignmentId - The assignment's id, for the warning logged when
 *   its list is malformed. Raw metadata documents do not carry it.
 * @param args.metadata - The assignment's metadata, converted or raw. A raw
 *   document with no `unitTestType` has it inferred as the converter would.
 *   Omitted, there are no milestones.
 * @return The validated milestones, in ladder order. Empty when the assignment
 *   declares none, is not console-graded, or declares a malformed list.
 */
export function getGradedMilestones({
  assignmentId,
  metadata,
}: {
  assignmentId: AssignmentId;
  metadata?:
    | (Partial<Omit<AssignmentMetadata, 'milestones'>> & {milestones?: unknown})
    | null;
}): AssignmentMilestone[] {
  if (isNullOrUndefined(metadata?.milestones)) {
    return [];
  }
  const unitTestType =
    metadata.unitTestType ?? inferUnitTestTypeFromMetadata(metadata);
  if (unitTestType !== 'console') {
    return [];
  }
  const parsed = AssignmentMilestonesSchema.safeParse(metadata.milestones);
  if (!parsed.success) {
    console.warn(
      `Ignoring invalid milestones on assignment ${assignmentId}: `,
      parsed.error.issues,
    );
    return [];
  }
  return parsed.data;
}

const ASSIGNMENT_METADATA_DOC_CONVERTER: FirestoreDataConverter<AssignmentMetadata> =
  {
    toFirestore(metadata: AssignmentMetadata): DocumentData {
      const toUpload = {...metadata, lastUpdated: Timestamp.now()};
      // The source of truth for the assignment ID is the document ID.
      delete toUpload.uid;
      return toUpload;
    },
    fromFirestore(
      snapshot: QueryDocumentSnapshot<DocumentData>,
      options: SnapshotOptions,
    ): AssignmentMetadata {
      const data = snapshot.data(options);
      if (!data) {
        console.debug('No data found in snapshot.');
        return null;
      }
      // The metadata document ID is the assignment ID.
      data.uid = snapshot.id;
      if (!data.unitTestType) {
        data.unitTestType = inferUnitTestTypeFromMetadata(data);
      }
      if (!data.unitTestBehavior) {
        data.unitTestBehavior = 'on_run';
      }
      if (!data.showSolutionBehavior) {
        data.showSolutionBehavior = inferShowSolutionBehavior(
          data as AssignmentMetadata,
        );
      }
      // Dropped rather than rejected, so a malformed list costs the student
      // the milestone UI and not the whole assignment.
      if (!isNullOrUndefined(data.milestones)) {
        const milestones = AssignmentMilestonesSchema.safeParse(
          data.milestones,
        );
        if (milestones.success) {
          data.milestones = milestones.data;
        } else {
          console.warn(
            `Ignoring invalid milestones on assignment ${snapshot.id}: `,
            milestones.error.issues,
          );
          delete data.milestones;
        }
      }
      if (data.canvasAssnId && typeof data.canvasAssnId !== 'number') {
        data.canvasAssnId = Number(data.canvasAssnId);
        if (isNaN(data.canvasAssnId)) {
          delete data.canvasAssnId;
        }
      }
      if (!isAssignmentMetadata(data)) {
        console.warn('Invalid data found in snapshot: ', data);
        return null;
      }
      return {
        ...data,
      };
    },
  };

export const getAssignmentsCollectionPath = (courseId: CourseId): string => {
  return `assns/${courseId}/assignments`;
};

export const getAssignmentDocumentPath = (
  courseId: CourseId,
  assignmentId: AssignmentId,
): string => {
  return `assns/${courseId}/assignments/${assignmentId}`;
};

export const getAssignmentSubDocumentsCollectionPath = (
  courseId: CourseId,
  assignmentId: AssignmentId,
): string => {
  return `assns/${courseId}/assignments/${assignmentId}/docs`;
};

export const getAssignmentPromptPath = (
  courseId: CourseId,
  assignmentId: AssignmentId,
): string => {
  return `assns/${courseId}/assignments/${assignmentId}/docs/prompt`;
};

export function getAssignmentMetadataCollection(
  courseId: CourseId,
): CollectionReference<AssignmentMetadata> {
  return collection(
    getFirestore(),
    getAssignmentsCollectionPath(courseId),
  ).withConverter(ASSIGNMENT_METADATA_DOC_CONVERTER);
}

export function getAssignmentMetadataReference(
  courseId: CourseId,
  assignmentId: AssignmentId,
): DocumentReference<AssignmentMetadata> {
  return doc(
    getFirestore(),
    getAssignmentDocumentPath(courseId, assignmentId),
  ).withConverter(ASSIGNMENT_METADATA_DOC_CONVERTER);
}

export function inferShowSolutionBehavior(
  metadata: AssignmentMetadata,
): ShowSolutionBehavior {
  if (!metadata) {
    return 'never';
  }
  if (metadata.showSolutionBehavior) {
    return metadata.showSolutionBehavior;
  }
  if (metadata.workedExample) {
    return 'always';
  }
  return 'never';
}
